import React, { useEffect, useState } from 'react';
import { getConfig } from '../config';
import { useNavigate } from 'react-router-dom';

const Card: React.FC<{ title: string; value: React.ReactNode; subtitle?: string; icon?: React.ReactNode }> = ({ title, value, subtitle, icon }) => (
  <div className="bg-white shadow p-4 rounded border border-gray-100 flex items-center gap-3 min-w-0">
    {icon && (
      <div className="shrink-0 w-10 h-10 rounded-md bg-mc-amber/15 text-mc-amber flex items-center justify-center">
        {icon}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="text-sm text-gray-500 break-words">{title}</div>
      <div className="text-2xl font-semibold leading-tight truncate">{value}</div>
      {subtitle && <div className="text-xs text-gray-400 break-words">{subtitle}</div>}
    </div>
  </div>
);

// Inline icons (brand-colored via currentColor)
const UsersIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16 11c1.66 0 3-1.57 3-3.5S17.66 4 16 4s-3 1.57-3 3.5S14.34 11 16 11zm-8 0c1.66 0 3-1.57 3-3.5S9.66 4 8 4 5 5.57 5 7.5 6.34 11 8 11zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.96 1.97 3.45V20h6v-3.5c0-2.33-4.67-3.5-7-3.5z"/>
  </svg>
);

const WorkspaceIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3 5h18v4H3V5zm0 6h8v8H3v-8zm10 0h8v8h-8v-8z"/>
  </svg>
);

const VideoIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17 10.5V7c0-1.1-.9-2-2-2H3C1.9 5 1 5.9 1 7v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-3.5l4 4v-11l-4 4z"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm.5 5h-2v6l5 3 .95-1.55-3.95-2.3V7z"/>
  </svg>
);

const CommentsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 2H4a2 2 0 00-2 2v14l4-4h14a2 2 0 002-2V4a2 2 0 00-2-2z"/>
  </svg>
);

const CameraIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 5h-3.2l-1.6-2H8.8L7.2 5H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-8 14a6 6 0 110-12 6 6 0 010 12zm0-2.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z"/>
  </svg>
);

const Dashboard: React.FC = () => {
  const { apiBase } = getConfig();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<{
    totalUsers: number;
    totalWorkspaces: number;
    totalRecordings: number;
    totalRecordingHours: number;
    totalComments: number;
    totalCameras: number;
    last30NewUsers: number;
    last30Recordings: number;
    last30RecordingHours: number;
    last30Comments: number;
    activeRecordings: number;
  } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/superadmin/analytics/dashboard-summary`, { credentials: 'include' });
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        setData(await res.json());
      } catch (e: any) {
        setErr(e?.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase]);

  if (loading) return <div>Loading…</div>;
  if (err) return <div className="text-red-600">Error: {err}</div>;
  if (!data) return null;

  return (
    <div className="container-page">
      <h1 className="text-2xl font-bold mb-4">Dashboard</h1>

      <h2 className="text-lg font-semibold mb-2">Quick insights</h2>
      <div className="grid grid-cols-1 landscape:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card title="Total Users" value={data.totalUsers} icon={<UsersIcon />} />
        <Card title="Total Workspaces" value={data.totalWorkspaces} icon={<WorkspaceIcon />} />
        <Card title="Total Recordings" value={data.totalRecordings} icon={<VideoIcon />} />
        <Card title="Total Recording Hours" value={data.totalRecordingHours.toFixed(1)} icon={<ClockIcon />} />
        <Card title="Total Comments" value={data.totalComments} icon={<CommentsIcon />} />
        <Card title="Total Cameras" value={data.totalCameras} icon={<CameraIcon />} />
      </div>

      <h2 className="text-lg font-semibold mb-2">Past 30 days</h2>
      <div className="grid grid-cols-1 landscape:grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card title="New Users" value={data.last30NewUsers} icon={<UsersIcon />} />
        <Card title="Recordings" value={data.last30Recordings} icon={<VideoIcon />} />
        <Card title="Recording Hours" value={data.last30RecordingHours.toFixed(1)} icon={<ClockIcon />} />
        <Card title="Comments" value={data.last30Comments} icon={<CommentsIcon />} />
      </div>

      <h2 className="text-lg font-semibold mb-2">Active Recordings</h2>
      <div className="bg-white shadow p-4 rounded flex items-center justify-between border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="shrink-0 w-10 h-10 rounded-md bg-mc-amber/15 text-mc-amber flex items-center justify-center">
            <VideoIcon />
          </div>
          <div>
          <div className="text-sm text-gray-500">Currently active</div>
          <div className="text-2xl font-semibold">{data.activeRecordings}</div>
          </div>
        </div>
        <button
          className="bg-blue-600 text-white text-sm rounded px-3 py-2"
          onClick={() => navigate('/analytics/active')}
        >
          View details
        </button>
      </div>
    </div>
  );
};

export default Dashboard;


